import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { getAuth, onAuthStateChanged, signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { getFirestore, doc, getDoc, setDoc, deleteDoc, updateDoc, collection, query, where, limit, getDocs, enableNetwork } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useNavigation } from './NavigationContext';
import Constants from 'expo-constants';
import { authService } from '../services/authService';
import { defaultPreferences } from '../constants/defaultPreferences';


const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const { navigateTo } = useNavigation();
  const maxRetries = 3;
  const retryDelay = 1000; // 1 second
  const lastUidRef = useRef(null);
  const didNavigateRef = useRef(false);
  // Dev mode auto-login disabled - users must login manually
  const [devModeUser, setDevModeUser] = useState(null);
  const isDev = process.env.NODE_ENV === 'development';


  const register = async (email, password) => {
    const { user, error } = await authService.register(email, password);

    return {user, error};
  };

  const signIn = async (email, password) => {
    const { user, error } = await authService.login(email, password);
    if (user) {
      setUser(user);
    }
    return {user, error};
  };

  const signInWithGoogleAuth = async () => {
    const { user, error } = await authService.signInWithGoogle();
    if (user) {
      setUser(user);
    }
    return { user, error };
  };

  const logout = async () => {
    const { error } = await authService.logout();
    setUser(null);
    lastUidRef.current = null;
    didNavigateRef.current = false;
    return { error };
  };

  const resetPassword = async (email) => {
    try {
      const auth = getAuth();
      await sendPasswordResetEmail(auth, email);
      return { success: true };
    } catch (error) {
      console.error('Error sending password reset email:', error);
      return { success: false, error: error.message };
    }
  };

  // Add network initialization
  useEffect(() => {
    const initializeFirestore = async () => {
      const db = getFirestore();
      try {
        await enableNetwork(db);
        console.log('✅ Firestore network enabled');
      } catch (error) {
        console.error('Error enabling Firestore network:', error);
      }
    };

    initializeFirestore();
  }, []);

  useEffect(() => {
    const auth = getAuth();
    let unsubscribed = false;
    
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (unsubscribed) return;
      
      try {
        if (firebaseUser) {
          const db = getFirestore();
          
          // Function to handle user data
          const handleUserData = async (retryCount = 0) => {
            if (unsubscribed) return;
            
            try {
              const userRef = doc(db, 'users', firebaseUser.uid);
              const userDoc = await getDoc(userRef);

              let userData = null;

              if (userDoc.exists()) {
                userData = userDoc.data();

                // Update last login timestamp (best-effort)
                try {
                  await updateDoc(userRef, { lastLogin: new Date() });
                } catch (updateError) {
                  console.warn('Could not update last login time:', updateError);
                }
              } else {
                // Adopt legacy admin-created record keyed by email into uid doc on first login
                try {
                  const emailKey = (firebaseUser.email || '').toLowerCase();
                  if (emailKey) {
                    const legacyRef = doc(db, 'users', emailKey);
                    const legacySnap = await getDoc(legacyRef);
                    if (legacySnap.exists()) {
                      const legacyData = legacySnap.data();
                      const merged = { ...legacyData, uid: firebaseUser.uid, email: firebaseUser.email };
                      await setDoc(userRef, merged, { merge: true });
                      userData = merged;
                    }
                  }
                } catch (adoptErr) {
                  console.warn('User adopt by email failed:', adoptErr);
                }
              }

              // Ensure we always set a user object so UI can proceed
              const mergedUser = userData ? { ...userData, uid: firebaseUser.uid } : {
                uid: firebaseUser.uid,
                displayName: firebaseUser.displayName || '',
                email: firebaseUser.email || '',
                phoneNumber: firebaseUser.phoneNumber || '',
                photoURL: firebaseUser.photoURL || ''
              };
              
              setUser(mergedUser);

              // Navigate to Home only on first sign-in transition
              if (userData?.suspended) {
                navigateTo('Suspended');
                didNavigateRef.current = true;
              } else if (lastUidRef.current !== firebaseUser.uid && !didNavigateRef.current) {
                navigateTo('Home');
                didNavigateRef.current = true;
              }
              lastUidRef.current = firebaseUser.uid;
            } catch (error) {
              console.error('Error accessing user document:', error);
              if (error.message.includes('offline') && retryCount < maxRetries) {
                await new Promise(resolve => setTimeout(resolve, retryDelay));
                return handleUserData(retryCount + 1);
              }
              else {
                throw error;
              }
            }
          };

          await handleUserData();
        } else {
          setUser(null);
          lastUidRef.current = null;
          didNavigateRef.current = false;
        }
      } catch (error) {
        console.error('AuthContext: Error in auth state change:', error);
        if (isDev) {
          console.warn('Development mode: Continuing despite error');
        } else {
          setUser(null);
        }
      } finally {
        setLoading(false);
      }
    });

    return () => {
      unsubscribed = true;
      unsubscribe();
    };
  }, [navigateTo]);

  // Dev mode auto-login (DISABLED - manual login required)
  // This was causing unintended auto-login behavior
  // useEffect(() => {
  //   const handleDevModeAutoLogin = async () => {
  //     if (isDev && devModeUser) {
  //       try {
  //         const auth = getAuth();
  //         const password = process.env.REACT_APP_DEV_PASSWORD;
  //         if (!password) {
  //           throw new Error('Development password not found');
  //         }

  //         if (!auth.currentUser || auth.currentUser.email !== devModeUser) {
  //           await signInWithEmailAndPassword(auth, devModeUser, password);
  //         }
  //       } catch (error) {
  //         console.error('Dev mode auto-login error:', error);
  //         localStorage.removeItem('devModeUser');
  //         setDevModeUser(null);
  //       }
  //     }
  //   };

  //   handleDevModeAutoLogin();
  // }, [devModeUser, isDev]);

  // Store devModeUser in localStorage (DISABLED)
  // useEffect(() => {
  //   if (isDev) {
  //     if (devModeUser) {
  //       localStorage.setItem('devModeUser', devModeUser);
  //     } else {
  //       localStorage.removeItem('devModeUser');
  //     }
  //   }
  // }, [devModeUser, isDev]);
  
  // Clear any existing devModeUser from localStorage on mount
  useEffect(() => {
    if (typeof window !== 'undefined' && window.localStorage) {
      const storedDevUser = localStorage.getItem('devModeUser');
      if (storedDevUser) {
        console.log('Clearing stored dev mode user');
        localStorage.removeItem('devModeUser');
      }
    }
  }, []);

  // Load user profile when user changes
  useEffect(() => {
    if (user?.uid) {
      loadUserProfile();
    } else {
      setUserProfile(null);
    }
  }, [user?.uid]);

  // Load user profile from Firestore
  const loadUserProfile = async () => {
    if (!user?.uid) return;
    
    setProfileLoading(true);
    
    try {
      const db = getFirestore();
      const userRef = doc(db, 'users', user.uid);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        // Merge with default preferences if some are missing
        const profileData = userSnap.data();
        const userProfileWithDefaults = {
          ...profileData,
          preferences: {
            ...defaultPreferences,
            ...profileData.preferences
          }
        };
        setUserProfile(userProfileWithDefaults);
      } else {
        // If no user profile exists, create with defaults
        const newUserProfile = {
          uid: user.uid,
          username: user.displayName || '',
          email: user.email || '',
          phoneNumber: user.phoneNumber || '',
          profilePhoto: user.photoURL || '',
          preferences: defaultPreferences
        };
        setUserProfile(newUserProfile);
        // Save the new profile to Firestore
        await setDoc(userRef, newUserProfile, { merge: true });
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
    } finally {
      setProfileLoading(false);
    }
  };

  // Save user profile to Firestore
  const saveUserProfile = async (updatedProfile) => {
    if (!user?.uid) {
      return { success: false, error: 'No user ID found' };
    }

    setProfileLoading(true);

    try {
      const db = getFirestore();
      const userRef = doc(db, 'users', user.uid);
      await setDoc(userRef, updatedProfile, { merge: true });
      
      setUserProfile(prev => ({ ...prev, ...updatedProfile }));
      return { success: true };
    } catch (error) {
      console.error('Error saving user profile:', error);
      return { success: false, error: error.message };
    } finally {
      setProfileLoading(false);
    }
  };

  // Upload profile photo to Firebase Storage (rebuilt for reliability)
  const uploadProfilePhoto = async (fileBlob, fileName) => {
    if (!user?.uid) {
      console.error('[uploadProfilePhoto] No user ID');
      return { success: false, error: 'No user ID found' };
    }

    console.log('[uploadProfilePhoto] Starting upload for user:', user.uid);
    setProfileLoading(true);
    
    try {
      if (Platform.OS === 'web') {
        window.dispatchEvent(new CustomEvent('toast', { detail: { type: 'info', text: 'Uploading photo…' } }));
      }
    } catch {}

    try {
      // Step 1: Compress image if too large (max 1MB, 800px width)
      let processedBlob = fileBlob;
      const maxSize = 1024 * 1024; // 1MB
      
      if (fileBlob.size > maxSize) {
        console.log('[uploadProfilePhoto] Compressing image from', fileBlob.size, 'bytes');
        try {
          const compressImage = (blob, maxWidth = 800) => new Promise((resolve, reject) => {
            const img = new Image();
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            
            img.onerror = () => reject(new Error('Image load error'));
            img.onload = () => {
              try {
                let { width, height } = img;
                if (width > maxWidth) {
                  height = (height * maxWidth) / width;
                  width = maxWidth;
                }
                canvas.width = width;
                canvas.height = height;
                ctx.drawImage(img, 0, 0, width, height);
                canvas.toBlob(
                  (compressed) => resolve(compressed || blob),
                  'image/jpeg',
                  0.85
                );
              } catch (err) {
                reject(err);
              }
            };
            img.src = URL.createObjectURL(blob);
          });
          
          processedBlob = await compressImage(fileBlob);
          console.log('[uploadProfilePhoto] Compressed to', processedBlob.size, 'bytes');
        } catch (compErr) {
          console.warn('[uploadProfilePhoto] Compression failed, using original:', compErr);
        }
      }

      // Step 2: Convert to base64
      const toBase64 = (blob) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const result = String(reader.result || '');
          resolve(result);
        };
        reader.onerror = () => reject(new Error('FileReader error'));
        reader.readAsDataURL(blob);
      });
      
      const dataUrl = await toBase64(processedBlob);
      console.log('[uploadProfilePhoto] Converted to base64, length:', dataUrl.length);

      // Step 3: Upload to server-side Firebase Storage endpoint
      const base = (
        (typeof process !== 'undefined' && process?.env?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
        || (Constants?.expoConfig?.extra?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
        || (Constants?.expoConfig?.extra?.FUNCTIONS_BASE_URL)
        || (Constants?.manifest?.extra?.FUNCTIONS_BASE_URL)
      );
      
      if (!base) {
        throw new Error('Functions base URL not configured');
      }

      console.log('[uploadProfilePhoto] Uploading to:', `${base}/uploadUserImage`);
      const uploadResponse = await fetch(`${base}/uploadUserImage`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          base64: dataUrl,
          contentType: processedBlob.type || 'image/jpeg',
          filename: `profile_${user.uid}_${Date.now()}.jpg`
        })
      });

      const responseText = await uploadResponse.text();
      console.log('[uploadProfilePhoto] Upload response status:', uploadResponse.status);

      let publicURL = '';
      if (uploadResponse.ok) {
        const responseData = JSON.parse(responseText);
        publicURL = responseData.url;
        console.log('[uploadProfilePhoto] Got public URL:', publicURL);
      } else {
        console.error('[uploadProfilePhoto] Upload failed:', responseText);
        throw new Error(`Upload failed: ${responseText}`);
      }

      // Step 4: Save URL to Firestore user document
      const db = getFirestore();
      const userRef = doc(db, 'users', user.uid);
      
      await setDoc(userRef, {
        profilePhoto: publicURL,
        photoURL: publicURL, // Store in both fields for compatibility
        updatedAt: new Date().toISOString()
      }, { merge: true });

      console.log('[uploadProfilePhoto] Saved to Firestore');

      // Step 5: Update all local state immediately
      setUser((prev) => prev ? { ...prev, profilePhoto: publicURL, photoURL: publicURL } : prev);
      setUserProfile((prev) => prev ? { ...prev, profilePhoto: publicURL, photoURL: publicURL } : prev);

      // Step 6: Reload profile from Firestore to ensure consistency
      await loadUserProfile();

      // Step 7: Show success toast
      if (Platform.OS === 'web') {
        window.dispatchEvent(new CustomEvent('toast', {
          detail: { type: 'success', text: 'Profile photo updated!' }
        }));
      }

      console.log('[uploadProfilePhoto] Upload complete');
      return { success: true, photoURL: publicURL };

    } catch (error) {
      console.error('[uploadProfilePhoto] Error:', error);
      
      if (Platform.OS === 'web') {
        window.dispatchEvent(new CustomEvent('toast', {
          detail: { type: 'error', text: `Upload failed: ${error.message}` }
        }));
      }
      
      return { success: false, error: error.message };
    } finally {
      setProfileLoading(false);
    }
  };

  // Remove profile photo (rebuilt)
  const removeProfilePhoto = async () => {
    if (!user?.uid) {
      console.error('[removeProfilePhoto] No user ID');
      return { success: false, error: 'No user ID found' };
    }

    console.log('[removeProfilePhoto] Removing photo for user:', user.uid);
    setProfileLoading(true);
    
    try {
      if (Platform.OS === 'web') {
        window.dispatchEvent(new CustomEvent('toast', { detail: { type: 'info', text: 'Removing photo…' } }));
      }
    } catch {}

    try {
      // Step 1: Clear from Firestore
      const db = getFirestore();
      const userRef = doc(db, 'users', user.uid);
      
      await setDoc(userRef, {
        profilePhoto: '',
        photoURL: '',
        updatedAt: new Date().toISOString()
      }, { merge: true });

      console.log('[removeProfilePhoto] Cleared from Firestore');

      // Step 2: Update all local state
      setUser((prev) => prev ? { ...prev, profilePhoto: '', photoURL: '' } : prev);
      setUserProfile((prev) => prev ? { ...prev, profilePhoto: '', photoURL: '' } : prev);

      // Step 3: Reload profile
      await loadUserProfile();

      // Step 4: Success toast
      if (Platform.OS === 'web') {
        window.dispatchEvent(new CustomEvent('toast', {
          detail: { type: 'success', text: 'Profile photo removed' }
        }));
      }

      console.log('[removeProfilePhoto] Complete');
      return { success: true };

    } catch (error) {
      console.error('[removeProfilePhoto] Error:', error);
      
      if (Platform.OS === 'web') {
        window.dispatchEvent(new CustomEvent('toast', {
          detail: { type: 'error', text: `Failed to remove photo: ${error.message}` }
        }));
      }
      
      return { success: false, error: error.message };
    } finally {
      setProfileLoading(false);
    }
  };

  // Update user preferences
  const updateUserPreferences = async (preferences) => {
    if (!user?.uid) {
      return { success: false, error: 'No user ID found' };
    }

    const updatedProfile = { preferences };
    return await saveUserProfile(updatedProfile);
  };

  return (
    <AuthContext.Provider value={{
      user,
      setUser,
      loading,
      devModeUser,
      setDevModeUser,
      logout,
      register,
      signIn,
      signInWithGoogleAuth,
      resetPassword,
      // User profile management
      userProfile,
      profileLoading,
      saveUserProfile,
      uploadProfilePhoto,
      removeProfilePhoto,
      updateUserPreferences,
      loadUserProfile,
      defaultPreferences
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within a AuthProvider');
  }
  return context;
}

